import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Search, ChevronDown, Mail } from "lucide-react";
import FormModal from "@/components/FormModal";
import logo from "@/assets/nextpredict-logo.webp";
import SearchOverlay from "@/components/SearchOverlay";
import ThemeToggle from "@/components/ThemeToggle";
import { useSiteSettingsContext } from "@/contexts/SiteSettingsContext";
import { useNavConfig } from "@/hooks/useNavConfig";

const newsCategories = [
  { label: "Trending", href: "/news" },
  { label: "Politics", href: "/news?cat=Politics" },
  { label: "Crypto", href: "/news?cat=Crypto" },
  { label: "Financial", href: "/news?cat=Financial" },
  { label: "Tech & AI", href: "/news?cat=Tech+%26+AI" },
  { label: "Culture", href: "/news?cat=Culture" },
  { label: "Regulation", href: "/news?cat=Regulation" },
  { label: "Industry", href: "/news?cat=Industry" },
];

// Fallback defaults (used only if children are missing from config)
const defaultAboutPages = [
  { label: "About Us", href: "/about" },
  { label: "Editorial Team", href: "/about/editorial-team" },
  { label: "Editorial Guidelines", href: "/about/editorial-guidelines" },
  { label: "Announcements", href: "/about/announcements" },
  { label: "Contact", href: "/contact" },
];

const defaultExploreChildren = [
  { label: "Prediction Markets", href: "/prediction-markets" },
  { label: "Market Movers", href: "/markets" },
  { label: "Community Predictions", href: "/scorecard" },
  { label: "Apps & Platforms", href: "/prediction-market-apps" },
  { label: "Legal Overview", href: "/legal" },
];

interface DropdownItem {
  label: string;
  href: string;
  external?: boolean;
}

interface DropdownProps {
  label: string;
  items: DropdownItem[];
  isActive: boolean;
}

const NavDropdown = ({ label, items, isActive }: DropdownProps) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1 text-sm font-semibold tracking-wide uppercase transition-colors ${
          isActive ? "text-primary" : "text-muted-foreground hover:text-foreground"
        }`}
      >
        {label}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-2 w-52 rounded-lg border border-border bg-card shadow-lg py-2 z-50">
          {items.map((item) =>
            item.external ? (
              <a
                key={item.href}
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-secondary/50 transition-colors"
              >
                {item.label}
              </a>
            ) : (
              <Link
                key={item.href}
                to={item.href}
                onClick={() => setOpen(false)}
                className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-secondary/50 transition-colors"
              >
                {item.label}
              </Link>
            ),
          )}
        </div>
      )}
    </div>
  );
};
const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [subscribeOpen, setSubscribeOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const location = useLocation();
  const { isPageVisible } = useSiteSettingsContext();
  const { data: navConfig } = useNavConfig();

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setMobileExpanded(null);
  }, [location.pathname]);

  const toggleMobileSection = (section: string) => {
    setMobileExpanded(mobileExpanded === section ? null : section);
  };

  return (
    <>
      <nav className="sticky top-0 z-50 border-b border-border bg-background/90 backdrop-blur-md">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-4">
          <Link to="/" className="shrink-0 flex items-center gap-3">
            <img src={logo} alt="NEXTPredict" width={132} height={32} className="h-8 logo-light-fix" />
            <span className="hidden xl:inline text-[11px] font-bold tracking-widest uppercase border-l border-border pl-3 text-gradient">
              The Front Page of Tomorrow
            </span>
          </Link>

          {/* Desktop nav */}
          {/* Master Coder Fix: Responsive gaps prevent squishing on laptops */}
          <div className="hidden md:flex items-center gap-4 lg:gap-6 xl:gap-8 min-h-[34px]">
            {(navConfig ?? []).map((navItem) => {
              /* ... Keep your exact existing navItem mapping logic here ... */
              /* (I'm omitting the map contents for brevity, leave your existing if/else statements exactly as they are) */

              if (!navItem.visible) return null;
              if (navItem.id === "news" && isPageVisible("/news")) {
                return (
                  <Link
                    key={navItem.id}
                    to="/news"
                    className={`text-sm font-semibold tracking-wide uppercase transition-colors ${location.pathname.startsWith("/news") ? "text-primary" : "text-muted-foreground hover:text-foreground"}`}
                  >
                    {navItem.label}
                  </Link>
                );
              }
              if (navItem.id === "summit" && isPageVisible("/summit")) {
                return (
                  <Link
                    key={navItem.id}
                    to="/summit"
                    className={`text-sm font-semibold tracking-wide uppercase transition-colors ${location.pathname === "/summit" ? "text-primary" : "text-muted-foreground hover:text-foreground"}`}
                  >
                    {navItem.label}
                  </Link>
                );
              }
              if (navItem.id === "learn" && isPageVisible("/learn")) {
                return (
                  <Link
                    key={navItem.id}
                    to="/learn"
                    className={`text-sm font-semibold tracking-wide uppercase transition-colors ${location.pathname.startsWith("/learn") ? "text-primary" : "text-muted-foreground hover:text-foreground"}`}
                  >
                    {navItem.label}
                  </Link>
                );
              }
              if (navItem.id === "explore") {
                const exploreChildren = (navItem.children ?? defaultExploreChildren).filter((i) =>
                  isPageVisible(i.href),
                );
                if (exploreChildren.length === 0) return null;
                const isActive = exploreChildren.some((c) => location.pathname === c.href);
                return (
                  <NavDropdown key={navItem.id} label={navItem.label} items={exploreChildren} isActive={isActive} />
                );
              }
              if (navItem.id === "about") {
                const aboutChildren = (navItem.children ?? defaultAboutPages).filter((i) => isPageVisible(i.href));
                if (aboutChildren.length === 0) return null;
                return (
                  <NavDropdown
                    key={navItem.id}
                    label={navItem.label}
                    items={aboutChildren}
                    isActive={location.pathname.startsWith("/about") || location.pathname === "/contact"}
                  />
                );
              }
              // Custom nav items
              if (navItem.custom) {
                if (navItem.children && navItem.children.length > 0) {
                  const isActive = navItem.children.some((c) => location.pathname === c.href);
                  return (
                    <NavDropdown
                      key={navItem.id}
                      label={navItem.label}
                      items={navItem.children.map((c) => ({ label: c.label, href: c.href, external: c.external }))}
                      isActive={isActive}
                    />
                  );
                }
                const linkClass = `text-sm font-semibold tracking-wide uppercase transition-colors ${
                  location.pathname === navItem.href ? "text-primary" : "text-muted-foreground hover:text-foreground"
                }`;
                if (navItem.external) {
                  return (
                    <a
                      key={navItem.id}
                      href={navItem.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={linkClass}
                    >
                      {navItem.label}
                    </a>
                  );
                }
                return (
                  <Link key={navItem.id} to={navItem.href} className={linkClass}>
                    {navItem.label}
                  </Link>
                );
              }
              return null;
            })}

            {/* 1. Subscribe Button (Moved to the left, removed hidden lg constraint) */}
            <button
              onClick={() => setSubscribeOpen(true)}
              className="inline-flex shrink-0 items-center gap-1.5 bg-primary text-primary-foreground font-semibold text-xs px-4 py-1.5 rounded-md hover:opacity-90 transition-opacity"
            >
              <Mail className="w-3.5 h-3.5" />
              Subscribe
            </button>

            {/* 2. Search Bar */}
            <button
              onClick={() => setSearchOpen(true)}
              className="flex items-center shrink-0 gap-2 rounded-lg border border-border bg-secondary/50 px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground hover:border-primary/40 transition-colors"
            >
              <Search className="w-4 h-4" />
              <span className="hidden lg:inline">Search</span>
            </button>

            {/* 4. Theme Toggle */}
            <ThemeToggle />
          </div>

          {/* Mobile buttons */}
          <div className="flex md:hidden items-center gap-2">
            <button
              onClick={() => setSubscribeOpen(true)}
              className="inline-flex items-center gap-1 bg-primary text-primary-foreground font-semibold text-[11px] px-2.5 py-1 rounded-md hover:opacity-90 transition-opacity"
            >
              <Mail className="w-3 h-3" />
              Subscribe
            </button>
            <button
              className="text-muted-foreground hover:text-foreground p-1"
              onClick={() => setSearchOpen(true)}
              aria-label="Search"
            >
              <Search className="w-5 h-5" />
            </button>
            <button className="text-foreground p-1" onClick={() => setMobileOpen(!mobileOpen)} aria-label="Toggle menu">
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile dropdown */}
        {mobileOpen && (
          <div className="md:hidden border-t border-border bg-background px-6 py-4 space-y-1">
            {(navConfig ?? []).map((navItem) => {
              if (!navItem.visible) return null;
              if (navItem.id === "news" && isPageVisible("/news")) {
                return (
                  <Link
                    key={navItem.id}
                    to="/news"
                    onClick={() => setMobileOpen(false)}
                    className="block py-2 text-sm font-semibold tracking-wide uppercase text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {navItem.label}
                  </Link>
                );
              }
              if (navItem.id === "summit" && isPageVisible("/summit")) {
                return (
                  <Link
                    key={navItem.id}
                    to="/summit"
                    onClick={() => setMobileOpen(false)}
                    className="block py-2 text-sm font-semibold tracking-wide uppercase text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {navItem.label}
                  </Link>
                );
              }
              if (navItem.id === "learn" && isPageVisible("/learn")) {
                return (
                  <Link
                    key={navItem.id}
                    to="/learn"
                    onClick={() => setMobileOpen(false)}
                    className="block py-2 text-sm font-semibold tracking-wide uppercase text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {navItem.label}
                  </Link>
                );
              }
              if (navItem.id === "explore") {
                const exploreChildren = (navItem.children ?? defaultExploreChildren).filter((i) =>
                  isPageVisible(i.href),
                );
                if (exploreChildren.length === 0) return null;
                return (
                  <div key={navItem.id}>
                    <button
                      onClick={() => toggleMobileSection("explore")}
                      className="flex items-center justify-between w-full py-2 text-sm font-semibold tracking-wide uppercase text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {navItem.label}
                      <ChevronDown
                        className={`w-4 h-4 transition-transform ${mobileExpanded === "explore" ? "rotate-180" : ""}`}
                      />
                    </button>
                    {mobileExpanded === "explore" && (
                      <div className="pl-4 space-y-1 pb-2">
                        {exploreChildren.map((item) => (
                          <Link
                            key={item.href}
                            to={item.href}
                            onClick={() => setMobileOpen(false)}
                            className="block py-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
                          >
                            {item.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }
              if (navItem.id === "about") {
                const aboutChildren = (navItem.children ?? defaultAboutPages).filter((i) => isPageVisible(i.href));
                if (aboutChildren.length === 0) return null;
                return (
                  <div key={navItem.id}>
                    <button
                      onClick={() => toggleMobileSection("about")}
                      className="flex items-center justify-between w-full py-2 text-sm font-semibold tracking-wide uppercase text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {navItem.label}
                      <ChevronDown
                        className={`w-4 h-4 transition-transform ${mobileExpanded === "about" ? "rotate-180" : ""}`}
                      />
                    </button>
                    {mobileExpanded === "about" && (
                      <div className="pl-4 space-y-1 pb-2">
                        {aboutChildren.map((item) => (
                          <Link
                            key={item.href}
                            to={item.href}
                            onClick={() => setMobileOpen(false)}
                            className="block py-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
                          >
                            {item.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }
              // Custom nav items (mobile)
              if (navItem.custom) {
                const mobileClass =
                  "block py-2 text-sm font-semibold tracking-wide uppercase text-muted-foreground hover:text-foreground transition-colors";
                // Custom dropdown with children
                if (navItem.children && navItem.children.length > 0) {
                  return (
                    <div key={navItem.id}>
                      <button
                        onClick={() => toggleMobileSection(navItem.id)}
                        className="flex items-center justify-between w-full py-2 text-sm font-semibold tracking-wide uppercase text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {navItem.label}
                        <ChevronDown
                          className={`w-4 h-4 transition-transform ${mobileExpanded === navItem.id ? "rotate-180" : ""}`}
                        />
                      </button>
                      {mobileExpanded === navItem.id && (
                        <div className="pl-4 space-y-1 pb-2">
                          {navItem.children.map((child) =>
                            child.external ? (
                              <a
                                key={child.href}
                                href={child.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => setMobileOpen(false)}
                                className="block py-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
                              >
                                {child.label}
                              </a>
                            ) : (
                              <Link
                                key={child.href}
                                to={child.href}
                                onClick={() => setMobileOpen(false)}
                                className="block py-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
                              >
                                {child.label}
                              </Link>
                            ),
                          )}
                        </div>
                      )}
                    </div>
                  );
                }
                // Custom single link
                if (navItem.external) {
                  return (
                    <a
                      key={navItem.id}
                      href={navItem.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setMobileOpen(false)}
                      className={mobileClass}
                    >
                      {navItem.label}
                    </a>
                  );
                }
                return (
                  <Link key={navItem.id} to={navItem.href} onClick={() => setMobileOpen(false)} className={mobileClass}>
                    {navItem.label}
                  </Link>
                );
              }
              return null;
            })}
          </div>
        )}
      </nav>

      {searchOpen && <SearchOverlay onClose={() => setSearchOpen(false)} />}
      <FormModal
        isOpen={subscribeOpen}
        onClose={() => setSubscribeOpen(false)}
        title="Join the inner circle."
        subtitle="Get weekly prediction markets intelligence that the crowd does not see. Curated news, exclusive analysis, and early access to NEXTPredict research."
        incentive="🎁 Subscribers get first access to our reports, event invites, and summit ticket drops."
        formId="7e677eab-387c-4053-af96-df54a2e31344"
      />
    </>
  );
};

export default Navbar;
